import * as React from 'react';
import { withStyles, StyleRulesCallback, WithStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { IObservableValue, observable, observe } from 'mobx';
import { defaultPageable, emptyPage, Page, Pageable } from '../../api/pageable';
import PaginationNavigation from '../../components/PaginationNavigation';
import { createModule, getClusters } from './questionCatalogueApi';
import { Spinner } from '../../spinner/Spinner';
import { translation } from '../../i18n';
import { TextField } from '@material-ui/core';
import { List } from '@material-ui/core';
import { ListItem } from '@material-ui/core';
import { Cancel as CancelIcon } from '@material-ui/icons';
import { Save as SaveIcon } from '@material-ui/icons';
import { ListItemText } from '@material-ui/core';
import { ListItemSecondaryAction } from '@material-ui/core';
import { Button } from '@material-ui/core';
import { QuestionClusterDto } from '../questionClusterTypes';
import QuestionClusterPreview from '../../components/preview/QuestionClusterPreview';
import { log } from '../../logConfig';
import { emptyValidationErrors, isValidationErrors, ValidationErrors } from '../../api/restApi';
import { emptyQuestionModuleRequest, QuestionModuleRequest } from '../questionModuleTypes';
import { createPicker } from '../../util/picker';
import { renderRemainingErrors } from '../../util/formUtils';
import { Typography } from '@material-ui/core';
import { isValidQuestionModule } from '../../util/validations';
import EmptyPage from '../../components/emptyPage/EmptyPage';
import { green } from '@material-ui/core/colors';
import { Snackbar } from '@material-ui/core';
import { SnackbarContent } from '@material-ui/core';

interface Props {
  renderSearch: (showSearch: boolean) => void;
  searchText: IObservableValue<string>;
}

type LocalStyles = 'previewArea' | 'success';

const localStyles: StyleRulesCallback<LocalStyles> = theme => ({

  previewArea: {
    padding: theme.spacing.unit * 2,
  },
  success: {
    backgroundColor: green[600],
  }

});

type PropsWithStyle = WithStyles<LocalStyles> & Props;

@observer
class ModuleTab extends React.Component<PropsWithStyle, {}> {

  @observable private loading: boolean = true;
  @observable private clusters: Page<QuestionClusterDto> = emptyPage();
  @observable private pageAble: Pageable = defaultPageable();
  @observable private selectedClusters: QuestionClusterDto[] = [];
  @observable private newModule: QuestionModuleRequest = emptyQuestionModuleRequest();
  @observable private validationErrors: ValidationErrors = emptyValidationErrors();
  private expanded: IObservableValue<string> = observable.box('');
  private saveLoading: IObservableValue<boolean> = observable.box(false);
  private displaySnackbar: IObservableValue<boolean> = observable.box(false);

  constructor(props: PropsWithStyle) {
    super(props);
    this.props.renderSearch(true);
    this.onSaveClicked = this.onSaveClicked.bind(this);
  }

  public componentDidMount(): void {
    observe(
      this.props.searchText,
      () => {
        this.loadClusters();
        log.info('Module ' + this.props.searchText.get())
      }
    );
    this.loadClusters();
  }

  public render() {
    const errorPicker = createPicker(this.validationErrors.fieldErrors);
    return (
      <>
        {
          this.saveLoading.get() ?
            <Spinner/> :
            <>
              <div style={{width: '100%', textAlign: 'right'}}>
                <div>
                  <Button onClick={this.onSaveClicked}><SaveIcon/> {translation.t('actions.save')}</Button>
                </div>
              </div>
              <div>
                <TextField
                  label={translation.t('module.title')}
                  fullWidth={true}
                  value={this.newModule.title}
                  onChange={ev => {
                    this.newModule.title = ev.target.value
                  }}
                />
                <TextField
                  label={translation.t('module.description')}
                  multiline={true}
                  fullWidth={true}
                  value={this.newModule.description}
                  onChange={ev => {
                    this.newModule.description = ev.target.value
                  }}
                />
                <Typography variant={'subtitle1'}>{translation.t('module.cluster')}:</Typography>
                {
                  this.selectedClusters.length === 0 ?
                    <>
                      <Typography>{translation.t('createCatalogueComponent.noClusterSelected')}</Typography>
                      {renderRemainingErrors(errorPicker.pick(e => e.field === 'cluster'))}
                    </>
                    :
                    <List>
                      {this.selectedClusters.map((cluster, index) => (
                        <ListItem
                          key={cluster.id + '' + index}
                          button={false}
                        >
                          <ListItemText primary={this.getClusterTitle(cluster)}/>
                          <ListItemSecondaryAction>
                            <Button
                              onClick={() => this.selectedClusters.splice(this.selectedClusters.indexOf(cluster), 1)}>
                              <CancelIcon/>
                            </Button>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                }
                {renderRemainingErrors(errorPicker.remaining())}
              </div>
              <div className={this.props.classes.previewArea}>
                {this.loading ?
                  <Spinner/> :
                  <>
                    {this.clusters.content.length === 0 ?
                      <EmptyPage message={translation.t('noEntry.questionCatalogue.title')}
                                 searchText={this.props.searchText.get()}
                                 displayFab={false}/> :
                      <>
                        {this.clusters.content.map(cluster =>
                          <QuestionClusterPreview key={cluster.id} cluster={cluster} expanded={this.expanded}
                                                  handleAdd={this.handleAdd}/>
                        )}
                        <PaginationNavigation
                          totalPages={this.clusters.totalPages}
                          page={this.clusters.pageable.pageNumber!!}
                          onChangePage={this.handleChangePage}/>
                      </>
                    }
                  </>
                }
              </div>
              <Snackbar
                className={this.props.classes.success}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                onClose={() => this.displaySnackbar.set(false)}
                open={this.displaySnackbar.get()}
                autoHideDuration={3000}
                ContentProps={{
                  'aria-describedby': 'message-id',
                }}
              >
                <SnackbarContent className={this.props.classes.success} message={translation.t('moduleTab.saved')}/>
              </Snackbar>
            </>
        }
      </>
    );
  }

  private getClusterTitle = (cluster: QuestionClusterDto): string => {
    if (cluster.title) {
      return cluster.title
    } else {
      return translation.t('createCatalogueComponent.noClusterTitle')
    }
  };

  private onSaveClicked() {
    this.selectedClusters.map((cluster, index) => {
      this.newModule.clusters.push({position: index + 1, questionCluster: cluster});
    });

    this.validationErrors = isValidQuestionModule(this.newModule);
    if (this.validationErrors === undefined || this.validationErrors.fieldErrors.length === 0) {
      this.saveLoading.set(true);
      createModule(this.newModule).then(
        data => {
          this.saveLoading.set(false);
          if (isValidationErrors(data)) {
            this.validationErrors = data;
          } else {
            this.validationErrors = emptyValidationErrors();
            this.displaySnackbar.set(true);
            this.newModule.title = '';
            this.newModule.description = '';
            this.newModule.clusters = [];
            this.selectedClusters = [];
          }
        }
      )
    }
  }

  private handleAdd = (cluster: QuestionClusterDto) => {
    this.selectedClusters.push(cluster)
  };

  private handleChangePage = (event: any, page: number) => {
    this.pageAble.page = page;
    this.loadClusters();
  };

  private loadClusters = () => {
    this.loading = true;
    getClusters({searchText: this.props.searchText.get()}, this.pageAble).then(page => {
      this.clusters = page;
      this.loading = false;
    });
  };

}

export default withStyles(localStyles)(ModuleTab);
